import "@/App.css";
import { Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { Toaster } from "./components/ui/sonner";
import { lazyWithRetry as lazy } from "./lib/lazyWithRetry";

// Eager: critical shell (first paint)
import LoginPage from "./pages/LoginPage";
import DashboardLayout from "./layouts/DashboardLayout";
import ShopLayout from "./layouts/ShopLayout";
import B2bLayout from "./layouts/B2bLayout";

// Lazy: admin pages (route-based code splitting)
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const CategoriesPage = lazy(() => import("./pages/CategoriesPage"));
const CustomersPage = lazy(() => import("./pages/CustomersPage"));
const QuotationsPage = lazy(() => import("./pages/QuotationsPage"));
const InventoryPage = lazy(() => import("./pages/InventoryPage"));
const OrdersPage = lazy(() => import("./pages/OrdersPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const DiscountsPage = lazy(() => import("./pages/DiscountsPage"));
const AuditLogPage = lazy(() => import("./pages/AuditLogPage"));
const POSPage = lazy(() => import("./pages/POSPage"));
const CreditsPage = lazy(() => import("./pages/CreditsPage"));
const SalesHistoryPage = lazy(() => import("./pages/SalesHistoryPage"));
const ReturnsPage = lazy(() => import("./pages/ReturnsPage"));
const InvoiceSettingsPage = lazy(() => import("./pages/InvoiceSettingsPage"));
const ShopConfigPage = lazy(() => import("./pages/admin/ShopConfigPage"));
const BranchesPage = lazy(() => import("./pages/admin/BranchesPage"));
const StockTransfersPage = lazy(() => import("./pages/admin/StockTransfersPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const InventoryScanPage = lazy(() => import("./pages/InventoryScanPage"));
const EquipmentLookupPage = lazy(() => import("./pages/EquipmentLookupPage"));
const ProductHistoryPage = lazy(() => import("./pages/ProductHistoryPage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const SuppliersPage = lazy(() => import("./pages/SuppliersPage"));
const LostDamagedPage = lazy(() => import("./pages/LostDamagedPage"));
const InventoryAgingPage = lazy(() => import("./pages/InventoryAgingPage"));
const PurchasesPage = lazy(() => import("./pages/PurchasesPage"));
const CorporateCreditsPage = lazy(() => import("./pages/CorporateCreditsPage"));
const FinancePage = lazy(() => import("./pages/FinancePage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const TagsPage = lazy(() => import("./pages/TagsPage"));
const MarketplacePage = lazy(() => import("./pages/MarketplacePage"));

// Lazy: Public Shop
const ShopHomePage = lazy(() => import("./pages/shop/ShopHomePage"));
const ShopProductsPage = lazy(() => import("./pages/shop/ShopProductsPage"));
const ShopBrandsPage = lazy(() => import("./pages/shop/ShopBrandsPage"));
const ShopProductDetailPage = lazy(() => import("./pages/shop/ShopProductDetailPage"));
const ShopCheckoutPage = lazy(() => import("./pages/shop/ShopCheckoutPage"));
const ShopCreditProductsPage = lazy(() => import("./pages/shop/ShopCreditProductsPage"));

// Lazy: B2B Wholesale Portal
const B2bLoginPage = lazy(() => import("./pages/b2b/B2bLoginPage"));
const B2bCatalogPage = lazy(() => import("./pages/b2b/B2bCatalogPage"));
const B2bCustomersPage = lazy(() => import("./pages/admin/B2bCustomersPage"));

const FullPageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900" data-testid="page-loader">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      <p className="text-slate-600 dark:text-slate-400">Cargando...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <FullPageLoader />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  useEffect(() => {
    // Successful load: clear any stale-chunk reload flag so future failures can retry.
    window.sessionStorage.removeItem("chunk_reload_attempt");
  }, []);

  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <Suspense fallback={<FullPageLoader />}>
            <Routes>
              {/* Auth */}
              <Route path="/login" element={<LoginPage />} />

              {/* Dashboard (Protected) */}
              <Route path="/admin" element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }>
                <Route index element={<DashboardPage />} />
                <Route path="products" element={<ProductsPage />} />
                <Route path="categories" element={<CategoriesPage />} />
                <Route path="customers" element={<CustomersPage />} />
                <Route path="quotations" element={<QuotationsPage />} />
                <Route path="inventory" element={<InventoryPage />} />
                <Route path="orders" element={<OrdersPage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="users" element={<UsersPage />} />
                <Route path="discounts" element={<DiscountsPage />} />
                <Route path="audit-log" element={<AuditLogPage />} />
                <Route path="pos" element={<POSPage />} />
                <Route path="credits" element={<CreditsPage />} />
                <Route path="sales-history" element={<SalesHistoryPage />} />
                <Route path="returns" element={<ReturnsPage />} />
                <Route path="invoice-settings" element={<InvoiceSettingsPage />} />
                <Route path="shop-config" element={<ShopConfigPage />} />
                <Route path="branches" element={<BranchesPage />} />
                <Route path="b2b-customers" element={<B2bCustomersPage />} />
                <Route path="stock-transfers" element={<StockTransfersPage />} />
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route path="inventory-scan" element={<InventoryScanPage />} />
                <Route path="equipment-lookup" element={<EquipmentLookupPage />} />
                <Route path="product-history" element={<ProductHistoryPage />} />
                <Route path="support" element={<SupportPage />} />
                <Route path="suppliers" element={<SuppliersPage />} />
                <Route path="lost-damaged" element={<LostDamagedPage />} />
                <Route path="inventory-aging" element={<InventoryAgingPage />} />
                <Route path="purchases" element={<PurchasesPage />} />
                <Route path="corporate-credits" element={<CorporateCreditsPage />} />
                <Route path="finance" element={<FinancePage />} />
                <Route path="product-tags" element={<TagsPage />} />
                <Route path="marketplace" element={<MarketplacePage />} />
                <Route path="settings" element={<SettingsPage />} />
              </Route>

              {/* Public Shop */}
              <Route path="/" element={<ShopLayout />}>
                <Route index element={<ShopHomePage />} />
                <Route path="productos" element={<ShopProductsPage />} />
                <Route path="marcas" element={<ShopBrandsPage />} />
                <Route path="credito" element={<ShopCreditProductsPage />} />
                <Route path="producto/:productId" element={<ShopProductDetailPage />} />
                <Route path="checkout" element={<ShopCheckoutPage />} />
              </Route>

              {/* B2B Wholesale Portal (Portal para Empresas) */}
              <Route path="/empresas" element={<B2bLayout />}>
                <Route index element={<B2bLoginPage />} />
                <Route path="catalogo" element={<B2bCatalogPage />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
